import { useState, useEffect } from "react";

import { RefreshCw, CheckCircle2, AlertCircle, Shield, Info } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface CommunicationCodeSelectorProps {
  onCodeSelected: (code: string) => void;
  suggestedCode?: string;
}

const ADJECTIVES = [
  "Swift", "Brave", "Bright", "Clever", "Cosmic", "Dancing", "Eager", "Fearless",
  "Gentle", "Happy", "Jolly", "Kind", "Lively", "Mighty", "Noble", "Quick",
  "Radiant", "Serene", "Spirited", "Stellar", "Tranquil", "Vibrant", "Wise", "Zen"
];

const NOUNS = [
  "Tiger", "Phoenix", "Dragon", "Eagle", "Falcon", "Hawk", "Lion", "Wolf",
  "Bear", "Panda", "Dolphin", "Whale", "Otter", "Fox", "Lynx", "Raven",
  "Owl", "Swan", "Crane", "Heron", "Mountain", "River", "Ocean", "Forest"
];

function generateCode(): string {
  const adjective = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)];
  return `${adjective} ${noun}`;
}

export const CommunicationCodeSelector = ({
  onCodeSelected,
  suggestedCode
}: CommunicationCodeSelectorProps) => {
  const [useCustom, setUseCustom] = useState(false);
  const [customCode, setCustomCode] = useState("");
  const [generatedCode, setGeneratedCode] = useState(suggestedCode || generateCode());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!useCustom) {
      onCodeSelected(generatedCode);
    }
  }, [generatedCode, useCustom, onCodeSelected]);

  const handleCustomCodeChange = (value: string) => {
    setCustomCode(value);
    setError(null);

    if (value.trim().length < 3) {
      setError("Code must be at least 3 characters");
      return;
    }

    if (value.trim().length > 50) {
      setError("Code must be 50 characters or less");
      return;
    }

    if (!/^[a-zA-Z0-9\s-]+$/.test(value)) {
      setError("Only letters, numbers, spaces, and hyphens allowed");
      return;
    }

    onCodeSelected(value.trim());
  };

  const handleRegenerate = () => {
    setGeneratedCode(generateCode());
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <Shield className="h-5 w-5 text-primary mt-0.5" />
        <div className="flex-1">
          <h3 className="font-semibold text-sm mb-1">Choose Your Security Code</h3>
          <p className="text-sm text-muted-foreground">
            This code will appear at the top of every email we send you, helping you identify authentic messages.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Generated Code Option */}
        <div
          className={cn(
            "border-2 rounded-lg p-4 cursor-pointer transition-all",
            !useCustom ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
          )}
          onClick={() => setUseCustom(false)}
        >
          <div className="flex items-center gap-2 mb-3">
            <div
              className={cn(
                "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                !useCustom ? "border-primary bg-primary" : "border-muted-foreground"
              )}
            >
              {!useCustom && <CheckCircle2 className="h-3 w-3 text-primary-foreground" />}
            </div>
            <Label className="text-base font-semibold cursor-pointer">Use Generated Code</Label>
            <Badge variant="secondary" className="ml-auto">Recommended</Badge>
          </div>

          <Card className="border-primary/30">
            <CardContent className="pt-6 text-center">
              <div className="text-2xl font-bold font-mono tracking-wide text-primary mb-3">
                {generatedCode}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRegenerate();
                }}
                disabled={useCustom}
              >
                <RefreshCw className="h-3 w-3 mr-2" />
                Generate Different Code
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Custom Code Option */}
        <div
          className={cn(
            "border-2 rounded-lg p-4 cursor-pointer transition-all",
            useCustom ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
          )}
          onClick={() => setUseCustom(true)}
        >
          <div className="flex items-center gap-2 mb-3">
            <div
              className={cn(
                "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                useCustom ? "border-primary bg-primary" : "border-muted-foreground"
              )}
            >
              {useCustom && <CheckCircle2 className="h-3 w-3 text-primary-foreground" />}
            </div>
            <Label className="text-base font-semibold cursor-pointer">Create Custom Code</Label>
          </div>

          <div className="space-y-2">
            <Input
              value={customCode}
              onChange={(e) => handleCustomCodeChange(e.target.value)}
              placeholder="Enter your own code..."
              disabled={!useCustom}
              className={cn(error && useCustom && "border-destructive")}
              onClick={(e) => e.stopPropagation()}
            />
            {error && useCustom && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {error}
              </p>
            )}
          </div>
        </div>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription className="text-xs">
          <strong>Why choose a code?</strong> Your code helps you spot phishing attempts.
          If an email claims to be from us but doesn't show your code, it's fake.
        </AlertDescription>
      </Alert>

      {/* Preview */}
      <div className="border border-border rounded-lg p-4 bg-muted/30">
        <p className="text-xs text-muted-foreground mb-2">Preview in emails:</p>
        <div className="bg-linear-to-r from-green-500 to-green-600 text-white p-3 rounded text-center">
          <div className="text-xs opacity-90 mb-1">🛡️ YOUR SECURITY CODE</div>
          <div className="text-lg font-bold tracking-wide">
            {useCustom ? (customCode.trim() || "Your Code") : generatedCode}
          </div>
          <div className="text-xs opacity-90 mt-1">All legitimate emails include this code</div>
        </div>
      </div>
    </div>
  );
};
